import type { Point } from '@/domain/world/geometry';
import type { ActorFootprint } from '@/domain/world/geometry';
import { footprintCollidesWithRegion } from '@/domain/world/collision';
import { resolveRegionMovement, type MovementResolution } from '@/domain/world/movement';
import type { RegionRegistry } from '@/domain/world/region-registry';
import type { RegionDefinition } from '@/domain/world/region-definition';

function continuousMovementRegion(registry: RegionRegistry): RegionDefinition {
  const regions = registry.all();
  const minX = Math.min(...regions.map(({ bounds }) => bounds.x));
  const minY = Math.min(...regions.map(({ bounds }) => bounds.y));
  const maxX = Math.max(...regions.map(({ bounds }) => bounds.x + bounds.w));
  const maxY = Math.max(...regions.map(({ bounds }) => bounds.y + bounds.h));
  return {
    id: 'continuous-world',
    biome: registry.regionAtWorldPoint({ x: minX, y: minY }).biome,
    bounds: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
    entrances: [],
    collision: regions.flatMap(({ collision }) => collision),
    zones: regions.flatMap(({ zones }) => zones),
    camera: { bounds: { x: minX, y: minY, w: maxX - minX, h: maxY - minY }, zoom: 1 },
    renderer: { kind: 'procedural', biome: 'meadow' },
  };
}

export function moveActorInActiveRegion(input: {
  registry: RegionRegistry;
  from: Point;
  velocity: Point;
  speed: number;
  dtSec: number;
  radius: number;
  footprint?: ActorFootprint;
  allowTransitions?: boolean;
}): MovementResolution {
  const activeRegion = input.registry.regionAtWorldPoint(input.from);
  const region = continuousMovementRegion(input.registry);
  const footprint = input.footprint ?? { radiusX: input.radius, radiusY: input.radius };
  const outsideFootprintBounds =
    input.from.x < region.bounds.x + footprint.radiusX ||
    input.from.x > region.bounds.x + region.bounds.w - footprint.radiusX ||
    input.from.y < region.bounds.y + footprint.radiusY ||
    input.from.y > region.bounds.y + region.bounds.h - footprint.radiusY;
  const mustRecover =
    outsideFootprintBounds || footprintCollidesWithRegion(region, input.from, footprint);
  const safeOrigin = mustRecover
    ? input.registry.nearestValidPoint(
        activeRegion.id,
        input.from,
        Math.max(footprint.radiusX, footprint.radiusY),
      )
    : input.from;
  const distance =
    Number.isFinite(input.speed) && Number.isFinite(input.dtSec)
      ? input.speed * input.dtSec
      : 0;

  const resolution = resolveRegionMovement({
    region,
    from: safeOrigin,
    desired: {
      x: safeOrigin.x + input.velocity.x * distance,
      y: safeOrigin.y + input.velocity.y * distance,
    },
    radius: input.radius,
    footprint,
  });

  return resolution;
}
